import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProjectDetailModal = ({ project, isOpen, onClose }) => {
  if (!project) return null;

  const handleLiveDemo = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleGitHub = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal Content */}
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className="relative bg-background rounded-lg border border-border max-w-4xl w-full max-h-[90vh] overflow-hidden"
            >
              {/* Header */}
              <div className="sticky top-0 bg-background border-b border-border p-6 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-text-primary">{project.title}</h2>
                  <p className="text-sm text-muted-foreground mt-1">{project.category} • {project.completedDate}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  iconName="X"
                  iconSize={20}
                />
              </div>

              {/* Content */}
              <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
                <div className="p-6">
                  {/* Project Image */}
                  <div className="mb-6">
                    <Image
                      src={project.image}
                      alt={project.title}
                      className="w-full h-64 object-cover rounded-lg"
                    />
                  </div>

                  {/* Quick Actions */}
                  <div className="flex flex-wrap gap-3 mb-6">
                    <Button
                      variant="default"
                      onClick={() => handleLiveDemo(project.liveUrl)}
                      iconName="ExternalLink"
                      iconPosition="right"
                    >
                      Live Demo
                    </Button>
                    {project.githubUrl && project.githubUrl !== "" && (
                      <Button
                        variant="outline"
                        onClick={() => handleGitHub(project.githubUrl)}
                        iconName="Github"
                        iconPosition="left"
                      >
                        View Code
                      </Button>
                    )}
                    <div className="flex items-center space-x-4 text-sm text-muted-foreground ml-auto">
                      <div className="flex items-center space-x-1">
                        <Icon name="Star" size={16} />
                        <span>{project.stars}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="GitFork" size={16} />
                        <span>{project.forks}</span>
                      </div>
                    </div>
                  </div>

                  {/* Project Overview */}
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-text-primary mb-3">Project Overview</h3>
                    <p className="text-text-secondary leading-relaxed">{project.fullDescription}</p>
                  </div>

                  {/* Problem & Solution */}
                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div>
                      <h4 className="text-md font-semibold text-text-primary mb-3 flex items-center">
                        <Icon name="AlertTriangle" size={18} className="mr-2 text-warning" />
                        Problem Statement
                      </h4>
                      <p className="text-text-secondary text-sm leading-relaxed">{project.problemStatement}</p>
                    </div>
                    <div>
                      <h4 className="text-md font-semibold text-text-primary mb-3 flex items-center">
                        <Icon name="Lightbulb" size={18} className="mr-2 text-success" />
                        Solution Approach
                      </h4>
                      <p className="text-text-secondary text-sm leading-relaxed">{project.solutionApproach}</p>
                    </div>
                  </div>

                  {/* Technical Details */}
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-text-primary mb-4">Technical Implementation</h3>
                    
                    {/* Technology Stack */}
                    <div className="mb-6">
                      <h4 className="text-md font-medium text-text-primary mb-3">Technology Stack</h4>
                      <div className="flex flex-wrap gap-2">
                        {project.technologies.map((tech, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 text-sm font-medium bg-primary/10 text-primary rounded-full"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Key Features */}
                    <div className="mb-6">
                      <h4 className="text-md font-medium text-text-primary mb-3">Key Features</h4>
                      <ul className="space-y-2">
                        {project.keyFeatures.map((feature, index) => (
                          <li key={index} className="flex items-start space-x-2 text-sm text-text-secondary">
                            <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Technical Challenges */}
                    <div>
                      <h4 className="text-md font-medium text-text-primary mb-3">Technical Challenges & Solutions</h4>
                      <div className="space-y-3">
                        {project.challenges.map((challenge, index) => (
                          <div key={index} className="bg-muted/50 rounded-lg p-4">
                            <h5 className="font-medium text-text-primary mb-2">{challenge.title}</h5>
                            <p className="text-sm text-text-secondary">{challenge.solution}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Project Metrics */}
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-text-primary mb-4">Project Metrics</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-muted/30 rounded-lg">
                        <div className="text-2xl font-bold text-primary">{project.duration}</div>
                        <div className="text-sm text-muted-foreground">Duration</div>
                      </div>
                      <div className="text-center p-4 bg-muted/30 rounded-lg">
                        <div className="text-2xl font-bold text-primary">{project.teamSize}</div>
                        <div className="text-sm text-muted-foreground">Team Size</div>
                      </div>
                      <div className="text-center p-4 bg-muted/30 rounded-lg">
                        <div className="text-2xl font-bold text-primary">{project.codeLines}</div>
                        <div className="text-sm text-muted-foreground">Lines of Code</div>
                      </div>
                      <div className="text-center p-4 bg-muted/30 rounded-lg">
                        <div className="text-2xl font-bold text-primary">{project.performance}</div>
                        <div className="text-sm text-muted-foreground">Performance</div>
                      </div>
                    </div>
                  </div>

                  {/* Screenshots */}
                  {project.screenshots && project.screenshots.length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-lg font-semibold text-text-primary mb-4">Screenshots</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        {project.screenshots.map((screenshot, index) => (
                          <div key={index} className="rounded-lg overflow-hidden border border-border">
                            <Image
                              src={screenshot.url}
                              alt={screenshot.caption}
                              className="w-full h-48 object-cover"
                            />
                            <div className="p-3 bg-muted/30">
                              <p className="text-sm text-text-secondary">{screenshot.caption}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ProjectDetailModal;